import type { AddTeamPersonBody, TeamPersonBody, TeamResponse } from '@pulse/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/** The Team page (ACS-001, ACS-002): admins only. */
export function useTeam() {
  return useQuery({ queryKey: ['team'], queryFn: () => apiRequest<TeamResponse>('/api/users'), staleTime: 30_000 });
}

export type TeamChange =
  | { readonly kind: 'add'; readonly body: AddTeamPersonBody }
  | { readonly kind: 'edit'; readonly userId: string; readonly body: TeamPersonBody }
  | { readonly kind: 'invite'; readonly userId: string }
  | { readonly kind: 'remove'; readonly userId: string };

/** Adding, editing, inviting or removing someone. The sidebar's own role may change, so the shell reloads too. */
export function useChangeTeam() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (change: TeamChange): Promise<unknown> => {
      if (change.kind === 'add') return apiRequest('/api/users', { method: 'POST', body: change.body });
      const path = `/api/users/${encodeURIComponent(change.userId)}`;
      if (change.kind === 'edit') return apiRequest(path, { method: 'PUT', body: change.body });
      if (change.kind === 'invite') return apiRequest(`${path}/invite`, { method: 'POST' });
      return apiRequest(path, { method: 'DELETE' });
    },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: ['team'] }), client.invalidateQueries({ queryKey: ['pages', 'shell'] })]),
  });
}
